import { useCallback, useMemo, useState } from 'react';
import type { BCPFieldConfig, BCPSectionConfig } from '../config/bcpFieldConfig';

export interface UseBCPDynamicFormOptions {
  sections: BCPSectionConfig[];
  initialValues?: Record<string, string>;
}

export interface BCPDynamicFormError {
  fieldId: string;
  message: string;
}

export interface UseBCPDynamicFormResult {
  values: Record<string, string>;
  errors: Record<string, string>;
  setFieldValue: (fieldId: string, value: string) => void;
  setValues: (nextValues: Record<string, string>) => void;
  reset: (nextValues?: Record<string, string>) => void;
  clearErrors: () => void;
  validate: () => boolean;
  getFieldValue: (fieldId: string) => string;
}

const isFieldRequired = (field: BCPFieldConfig): boolean => {
  return Boolean(field.required);
};

const buildDefaultValues = (
  sections: BCPSectionConfig[],
  providedValues?: Record<string, string>
): Record<string, string> => {
  const defaults: Record<string, string> = {};

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type === 'static') {
        return;
      }
      if (field.type === 'table') {
        field.table.rows.forEach((row, rowIndex) => {
          const columns = row.columnPlaceholders?.length ?? field.table.headers?.length ?? 0;
          for (let colIndex = 0; colIndex < columns; colIndex += 1) {
            const key = `${field.id}_${rowIndex}_${colIndex}`;
            const existing = providedValues?.[key];
            if (typeof existing === 'string') {
              defaults[key] = existing;
            }
          }
        });
      } else if (field.type === 'checklist') {
        field.checklistItems.forEach((_, itemIndex) => {
          const key = `${field.id}_${itemIndex}`;
          const existing = providedValues?.[key];
          defaults[key] = typeof existing === 'string' ? existing : 'false';
        });
      }

      const provided = providedValues?.[field.id];
      if (typeof provided === 'string') {
        defaults[field.id] = provided;
      } else if (field.defaultValue) {
        defaults[field.id] = field.defaultValue;
      } else {
        // Initialize with empty string if no provided value and no default value
        defaults[field.id] = '';
      }
    });
  });

  return {
    ...defaults,
    ...providedValues,
  };
};

const getFieldLabel = (field: BCPFieldConfig): string => {
  if (field.label) {
    return field.label;
  }
  return field.id;
};

const collectValidationErrors = (
  sections: BCPSectionConfig[],
  values: Record<string, string>
): Record<string, string> => {
  const nextErrors: Record<string, string> = {};

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (!isFieldRequired(field)) {
        return;
      }

      if (field.type === 'static') {
        return;
      }

      const value = values[field.id];

      if (!value || value.trim().length === 0) {
        nextErrors[field.id] = `${getFieldLabel(field)}は必須項目です。`;
      }
    });
  });

  return nextErrors;
};

export const useBCPDynamicForm = (
  options: UseBCPDynamicFormOptions
): UseBCPDynamicFormResult => {
  const { sections, initialValues } = options;

  const initialState = useMemo(
    () => buildDefaultValues(sections, initialValues),
    [sections, initialValues]
  );

  const [values, setValuesState] = useState<Record<string, string>>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setFieldValue = useCallback((fieldId: string, value: string) => {
    setValuesState((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    setErrors((prev) => {
      if (!prev[fieldId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const setValues = useCallback((nextValues: Record<string, string>) => {
    setValuesState((prev) => ({
      ...prev,
      ...nextValues,
    }));
  }, []);

  const reset = useCallback((nextValues?: Record<string, string>) => {
    setValuesState(buildDefaultValues(sections, nextValues));
    setErrors({});
  }, [sections]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const validate = useCallback(() => {
    const validationErrors = collectValidationErrors(sections, values);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [sections, values]);

  const getFieldValue = useCallback(
    (fieldId: string) => values[fieldId] ?? '',
    [values]
  );

  return {
    values,
    errors,
    setFieldValue,
    setValues,
    reset,
    clearErrors,
    validate,
    getFieldValue,
  };
};
